import React from 'react';

const IndexPageComponent = ({ projectTitle = "Water Network Project", organizationName = "Solarpunk Guild", activities = [], activityPagesCount = 0 }) => {
  // Calculate pagination for index content to prevent overflow
  const fixedLines = 4; // Title, Page number, Introduction, Activities header
  const dataVisualizationLines = 8; // Fixed data visualization entries
  
  // Increased capacity to 28 lines per page (10 more entries)
  const maxLinesPerPage = 28;
  
  // Calculate how many activities fit on first page (after fixed content and data viz)
  const availableLinesFirstPage = maxLinesPerPage - fixedLines - dataVisualizationLines;
  const activitiesOnFirstPage = Math.max(0, Math.min(availableLinesFirstPage, activities.length));
  
  // Calculate remaining activities for subsequent pages
  const remainingActivities = activities.slice(activitiesOnFirstPage);
  const maxActivitiesPerSubsequentPage = maxLinesPerPage - 2; // 2 lines for header
  
  // Split remaining activities across additional pages
  const additionalPages = [];
  let currentActivities = remainingActivities;
  
  while (currentActivities.length > 0) {
    const activitiesForThisPage = currentActivities.slice(0, maxActivitiesPerSubsequentPage);
    additionalPages.push(activitiesForThisPage);
    currentActivities = currentActivities.slice(maxActivitiesPerSubsequentPage);
  }
  
  // Calculate total index pages needed
  const totalIndexPages = 1 + additionalPages.length;
  
  // Calculate dynamic page numbers based on actual index pages
  const introductionPageNumber = totalIndexPages + 1;
  const activitiesStartPage = introductionPageNumber + 1;

  return (
    <>
      {/* First Index Page */}
      <div className="pdf-section pdf-header" style={{ paddingTop: '20px', paddingBottom: '40px', paddingLeft: '20px', paddingRight: '20px' }}>
        <div className="flex justify-between items-start mb-6">
          <div className="text-lg text-[#636363] font-medium">{projectTitle}</div>
          <div className="flex items-center">
            <div className="w-5 h-5 bg-green-600 rounded-full mr-2 flex items-center justify-center">
              <span className="text-white text-xs font-bold">S</span>
            </div>
            <span className="text-lg text-[#636363] font-medium">{organizationName}</span>
          </div>
        </div>
        
        <div className="mb-5">
          <h1 className="text-xl font-semibold text-textSecondary mb-2">Project report</h1>
          <h2 className="text-4xl font-bold text-textPrimary mb-2">{projectTitle}</h2>
          <div className="flex items-center">
            <span className="text-2xl font-semibold text-[#585757]">Initiated by</span>
            <div className="ml-[.625rem] flex items-center gap-[.625rem]">
              <div className="w-5 h-5 bg-green-600 rounded-full">
                <span className="text-white text-xs font-bold"></span>
              </div>
              <span className="text-2xl font-semibold text-[#585757]">{organizationName}</span>
            </div>
          </div>
        </div>

        {/* Table of Contents */}
        <div className='mb-4'>
          <h3 className="text-[1.755rem] font-semibold text-textPrimary mb-3">Index</h3>
          <div className="">
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-xl text-textPrimary">Title</span>
              <span className="font-semibold text-xl text-textPrimary">Page number</span>
            </div>
            <div className="flex justify-between mb-5">
              <span className='text-xl text-textPrimary'>Introduction</span>
              <span className='text-xl text-textPrimary'>{introductionPageNumber}</span>
            </div>
            
            {/* Activities on first page */}
            {activitiesOnFirstPage > 0 && (
              <div className="mb-5">
                <div className="font-semibold text-xl text-textPrimary mb-2">Activities</div>
                <div className="flex flex-col gap-2">
                  {activities.slice(0, activitiesOnFirstPage).map((activity, index) => (
                    <div key={activity._id || index} className="flex justify-between">
                      <span className='text-xl text-textPrimary'>{activity.title}</span>
                      <span className='text-xl text-textPrimary'>{Math.floor(index / 2) + activitiesStartPage}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Data Visualization section only on first page if no overflow */}
            {additionalPages.length === 0 && (
              <div className="">
                <div className="font-semibold text-xl text-textPrimary mb-2">Data Visualization</div>
                <div className="flex flex-col gap-2">
                  <div className="flex justify-between">
                    <span className='text-xl text-textPrimary'>Overview</span>
                    <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className='text-xl text-textPrimary'>Activities</span>
                    <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount + 1}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className='text-xl text-textPrimary'>Activity Types</span>
                    <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount + 2}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className='text-xl text-textPrimary'>Impact Certificates</span>
                    <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount + 3}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className='text-xl text-textPrimary'>Sustainable development goals (SDGs)</span>
                    <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount + 4}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className='text-xl text-textPrimary'>Contribution</span>
                    <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount + 5}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className='text-xl text-textPrimary'>Funds Deployed</span>
                    <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount + 6}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Additional Index Pages for remaining activities */}
      {additionalPages.map((pageActivities, pageIndex) => (
        <div key={pageIndex} className="pdf-section pdf-page-break" style={{ paddingTop: '20px', paddingBottom: '40px', paddingLeft: '20px', paddingRight: '20px' }}>
          <div className="flex justify-between items-start mb-6">
            <div className="text-lg text-[#636363] font-medium">{projectTitle}</div>
            <div className="flex items-center">
              <div className="w-5 h-5 bg-green-600 rounded-full mr-2 flex items-center justify-center">
                <span className="text-white text-xs font-bold">S</span>
              </div>
              <span className="text-lg text-[#636363] font-medium">{organizationName}</span>
            </div>
          </div>

          <div className='mb-4'>
            <h3 className="text-[1.755rem] font-semibold text-textPrimary mb-3">Index (continued)</h3>
            <div className="">
              <div className="mb-5">
                <div className="font-semibold text-xl text-textPrimary mb-2">Activities (continued)</div>
                <div className="flex flex-col gap-2">
                  {pageActivities.map((activity, index) => {
                    const globalIndex = activitiesOnFirstPage + (pageIndex * maxActivitiesPerSubsequentPage) + index;
                    return (
                      <div key={activity._id || index} className="flex justify-between">
                        <span className='text-xl text-textPrimary'>{activity.title}</span>
                        <span className='text-xl text-textPrimary'>{Math.floor(globalIndex / 2) + activitiesStartPage}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Data Visualization section on last index page */}
              {pageIndex === additionalPages.length - 1 && (
                <div className="">
                  <div className="font-semibold text-xl text-textPrimary mb-2">Data Visualization</div>
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between">
                      <span className='text-xl text-textPrimary'>Overview</span>
                      <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className='text-xl text-textPrimary'>Activities</span>
                      <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount + 1}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className='text-xl text-textPrimary'>Activity Types</span>
                      <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount + 2}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className='text-xl text-textPrimary'>Impact Certificates</span>
                      <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount + 3}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className='text-xl text-textPrimary'>Sustainable development goals (SDGs)</span>
                      <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount + 4}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className='text-xl text-textPrimary'>Contribution</span>
                      <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount + 5}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className='text-xl text-textPrimary'>Funds Deployed</span>
                      <span className='text-xl text-textPrimary'>{activitiesStartPage + activityPagesCount + 6}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default IndexPageComponent;
